<div class="place" data-testid="drawing-place-dialog">
  <header class="place-header">
    <h2 class="place-title">Place {{ title }} on the map</h2>
    <button type="button" mat-icon-button aria-label="Close" data-testid="drawing-place-close" (click)="close()"><mat-icon>close</mat-icon></button>
  </header>

  @if (phase === 'points') {
    <div class="place-panes">
      <section class="place-sheet" aria-label="Sheet">
        <div class="pane-bar">
          <div class="mode-chips" role="group" aria-label="Sheet tool">
            <button type="button" class="mode-chip" [class.mode-chip--on]="mode === 'points'" [attr.aria-pressed]="mode === 'points'"
              data-testid="drawing-place-mode-points" (click)="setMode('points')">Add points</button>
            @if (alignments.length) {
              <button type="button" class="mode-chip" [class.mode-chip--on]="mode === 'stations'" [attr.aria-pressed]="mode === 'stations'"
                data-testid="drawing-place-mode-stations" (click)="setMode('stations')">By stations</button>
            }
            <button type="button" class="mode-chip" [class.mode-chip--on]="mode === 'region'" [attr.aria-pressed]="mode === 'region'"
              data-testid="drawing-place-mode-region" (click)="setMode('region')">Draw area</button>
            @if (region) {
              <button type="button" class="link-button" data-testid="drawing-place-clear-region" (click)="clearRegion()">Whole page</button>
            }
          </div>
          <div class="zoom-tools">
            <button type="button" class="icon-button" aria-label="Zoom out" data-testid="drawing-place-zoom-out" [disabled]="zoom <= minZoom" (click)="zoomBy(-1)"><mat-icon>remove</mat-icon></button>
            <span class="zoom-text" data-testid="drawing-place-zoom">{{ zoom | number: '1.0-1' }}×</span>
            <button type="button" class="icon-button" aria-label="Zoom in" data-testid="drawing-place-zoom-in" [disabled]="zoom >= maxZoom" (click)="zoomBy(1)"><mat-icon>add</mat-icon></button>
          </div>
        </div>
        <!-- A pinch over the sheet zooms it at the cursor (never the page); zoomed in, a drag pans (Draw area: the drag draws) -->
        <div #sheetScroll class="sheet-scroll" data-testid="drawing-place-sheet-scroll" [class.sheet-scroll--panning]="sheetZoom.panning"
          (pointerdown)="sheetZoom.pointerDown($event)" (pointermove)="sheetZoom.pointerMove($event)" (pointerup)="sheetZoom.pointerUp($event)"
          (pointercancel)="sheetZoom.captureLost()" (lostpointercapture)="sheetZoom.captureLost()" (scroll)="scheduleSharp()">
          <div class="sheet-frame" [style.width.%]="zoom * 100" [class.sheet-frame--region]="mode === 'region'">
            <img #pageImage class="sheet-image" [src]="data.revision.preview_url" [alt]="title" draggable="false" data-testid="drawing-place-sheet"
              tabindex="0" role="button" [attr.aria-label]="'Sheet point ' + label(activePairs.length) + ': click, or use the arrow keys'"
              (load)="onPageLoad()" (click)="onSheetClick($event)" (keydown)="onSheetKey($event)" (mousedown)="onSheetMouseDown($event)" (dragstart)="$event.preventDefault()" />
            <!-- Zoomed in: the part in view redrawn sharp from the PDF (no pointer events: clicks reach the image) -->
            <div #sharpHost class="sheet-sharp" aria-hidden="true"></div>
            @if (region) {
              <div class="region" data-testid="drawing-place-region" [style.left.%]="region.x0 * 100" [style.top.%]="region.y0 * 100"
                [style.width.%]="(region.x1 - region.x0) * 100" [style.height.%]="(region.y1 - region.y0) * 100"></div>
            }
            @for (p of activePairs; track $index) {
              <span class="dot" [class.dot--check]="p.check" [style.left.%]="p.sheet.x * 100" [style.top.%]="p.sheet.y * 100">{{ label($index) }}</span>
            }
            @if (pendingSheet) {
              <span class="dot dot--pending" [class.dot--check]="nextIsCheck" data-testid="drawing-place-pending"
                [style.left.%]="pendingSheet.x * 100" [style.top.%]="pendingSheet.y * 100">{{ label(activePairs.length) }}</span>
            }
          </div>
        </div>
      </section>

      <section class="place-map" aria-label="Project map" data-testid="drawing-place-map">
        <app-esri-map [center]="center" [zoom]="17" (viewLoaded)="onViewLoaded($event)"></app-esri-map>
        <app-map-controls
          [contained]="true"
          [organizationBasemaps]="organizationBasemaps"
          [basemapId]="basemapId"
          [projectLayers]="layersToolbar"
          (projectLayerToggle)="onLayerToggle($event)"
          (projectLayerOpacity)="onLayerOpacity($event)"
          (projectLayerZoom)="zoomToLayer($event)"
          (basemapChanged)="onBasemapChanged($event)"
          (zoomIn)="zoomMapBy(1)"
          (zoomOut)="zoomMapBy(-1)"
        />
        <div class="map-hint mat-body-1" data-testid="drawing-place-hint">{{ hint }}</div>
        @if (hoverReadout) {
          <div class="map-readout" data-testid="drawing-place-readout" [style.left.px]="hoverReadout.x + 14" [style.top.px]="hoverReadout.y + 14">{{ hoverReadout.text }}</div>
        }
      </section>
    </div>

    <div class="place-points">
      @if (method === 'stations') {
        <!-- The app's underline fields; Side is the toolbar's segmented pill at the fields' height, all on one line -->
        <div class="station-form" data-testid="drawing-place-station-form">
          @if (alignments.length > 1) {
            <label class="field field--select">
              <span class="field-label">Alignment</span>
              <select class="field-input" data-testid="drawing-place-alignment" [ngModel]="alignmentIndex" (ngModelChange)="chooseAlignment($event)">
                <option [ngValue]="null" disabled>Choose an alignment</option>
                @for (a of alignments; track $index) { <option [ngValue]="$index">{{ a.name || 'Unnamed' }}</option> }
              </select>
            </label>
          }
          <label class="field">
            <span class="field-label">Station of point {{ label(stationPairs.length) }}</span>
            <input class="field-input" type="text" data-testid="drawing-place-station" placeholder="1018+50" maxlength="20" [ngModel]="stationText"
              (ngModelChange)="onStationInput($event)" [disabled]="!pendingSheet || !alignment" (keydown.enter)="addStation()" />
          </label>
          <label class="field field--short">
            <span class="field-label">Offset (ft)</span>
            <input class="field-input" type="number" data-testid="drawing-place-offset" min="0" max="10000" [ngModel]="offsetText"
              [class.field-input--muted]="sideChoice === 'on'" (ngModelChange)="onOffsetInput($event)" [disabled]="!pendingSheet || !alignment"
              (keydown.enter)="addStation()" />
          </label>
          <div class="field field--side">
            <span class="field-label" id="drawing-place-side-label">Side</span>
            <div class="side-toggle" role="group" aria-labelledby="drawing-place-side-label" data-testid="drawing-place-side"
              [class.side-toggle--needed]="sideHelp.needsSide && !!pendingSheet">
              @for (o of sideOptions; track o.value) {
                <button type="button" [class.active]="sideChoice === o.value" [attr.aria-pressed]="sideChoice === o.value" [disabled]="!pendingSheet || !alignment"
                  [attr.data-testid]="'drawing-place-side-' + o.value" (click)="pickSide(o.value)">{{ o.label }}</button>
              }
            </div>
            @if (sideHelp.text && pendingSheet) {
              <span class="field-help" [class.field-help--needed]="sideHelp.needsSide" data-testid="drawing-place-side-help">{{ sideHelp.text }}</span>
            }
          </div>
          <app-secondary-button buttonId="drawing-place-add-station" data-testid="drawing-place-add-station" [isDisabled]="!canAddStation" (handleClick)="addStation()">Add station</app-secondary-button>
        </div>
        @if (alignment) {
          <p class="station-note mat-caption" data-testid="drawing-place-station-range">{{ alignment.name || 'The alignment' }} runs {{ alignmentRangeText }}.
            @if (alignmentChainage) { <span data-testid="drawing-place-station-chainage">Stations measured along the line; plan stations unavailable</span> }
          </p>
        }
        @if (alignmentProblem) { <p class="error mat-body-1" role="alert" data-testid="drawing-place-alignment-problem">{{ alignmentProblem }}</p> }
      }
      <div class="points-bar">
        <mat-checkbox color="primary" [checked]="nextIsCheck" (change)="toggleCheck()" data-testid="drawing-place-check-toggle">Next point is a check point</mat-checkbox>
        @if (pendingSheet) {
          <button type="button" class="link-button" (click)="cancelPending()">Cancel point {{ label(activePairs.length) }}</button>
        }
        <span class="fit mat-body-1" data-testid="drawing-place-fit">
          @if (fit) {
            Fit: RMS {{ fit.rms_m | number: '1.2-2' }} m
            @if (fit.check_error_m !== null) { · Check {{ fit.check_error_m | number: '1.1-1' }} m }
            @if (fitApproximate) { <span class="chip chip--approximate">Approximate</span> }
            @if (needsCheckPoint) { <span data-testid="drawing-place-needs-check">Add a check point to verify</span> }
          } @else if (method === 'stations') {
            Add 2 stations to preview the fit.
          } @else {
            Add 3 control points to preview the fit.
          }
        </span>
      </div>
      @if (method === 'stations' && stationPairs.length) {
        <table mat-table [dataSource]="stationPairs" class="pairs-table" data-testid="drawing-place-station-pairs">
          <ng-container matColumnDef="index"><th mat-header-cell *matHeaderCellDef>#</th><td mat-cell *matCellDef="let p; let i = index">{{ label(i) }}</td></ng-container>
          <ng-container matColumnDef="kind"><th mat-header-cell *matHeaderCellDef>Type</th><td mat-cell *matCellDef="let p">{{ p.check ? 'Check' : 'Control' }}</td></ng-container>
          <ng-container matColumnDef="sheet"><th mat-header-cell *matHeaderCellDef>Sheet</th>
            <td mat-cell *matCellDef="let p">{{ p.sheet.x * 100 | number: '1.1-1' }}%, {{ p.sheet.y * 100 | number: '1.1-1' }}%</td></ng-container>
          <ng-container matColumnDef="station"><th mat-header-cell *matHeaderCellDef>Station</th>
            <td mat-cell *matCellDef="let p">{{ stationLabel(p) }}@if (!p.map) { <span class="error"> · not on this alignment</span> }</td></ng-container>
          <ng-container matColumnDef="remove"><th mat-header-cell *matHeaderCellDef></th>
            <td mat-cell *matCellDef="let p; let i = index"><button type="button" mat-icon-button [attr.aria-label]="'Remove point ' + label(i)" (click)="removePair(i)"><mat-icon>delete_outline</mat-icon></button></td></ng-container>
          <tr mat-header-row *matHeaderRowDef="stationColumns"></tr><tr mat-row *matRowDef="let row; columns: stationColumns"></tr>
        </table>
      } @else if (method === 'points' && pairs.length) {
        <table mat-table [dataSource]="pairs" class="pairs-table" data-testid="drawing-place-pairs">
          <ng-container matColumnDef="index"><th mat-header-cell *matHeaderCellDef>#</th><td mat-cell *matCellDef="let p; let i = index">{{ label(i) }}</td></ng-container>
          <ng-container matColumnDef="kind"><th mat-header-cell *matHeaderCellDef>Type</th><td mat-cell *matCellDef="let p">{{ p.check ? 'Check' : 'Control' }}</td></ng-container>
          <ng-container matColumnDef="sheet"><th mat-header-cell *matHeaderCellDef>Sheet</th>
            <td mat-cell *matCellDef="let p">{{ p.sheet.x * 100 | number: '1.1-1' }}%, {{ p.sheet.y * 100 | number: '1.1-1' }}%</td></ng-container>
          <ng-container matColumnDef="map"><th mat-header-cell *matHeaderCellDef>Map</th>
            <td mat-cell *matCellDef="let p">{{ p.map.lat | number: '1.6-6' }}, {{ p.map.lon | number: '1.6-6' }}</td></ng-container>
          <ng-container matColumnDef="remove"><th mat-header-cell *matHeaderCellDef></th>
            <td mat-cell *matCellDef="let p; let i = index"><button type="button" mat-icon-button [attr.aria-label]="'Remove point ' + label(i)" (click)="removePair(i)"><mat-icon>delete_outline</mat-icon></button></td></ng-container>
          <tr mat-header-row *matHeaderRowDef="columns"></tr><tr mat-row *matRowDef="let row; columns: columns"></tr>
        </table>
      }
      @if (chooseLayer) {
        <label class="field field--select layer-choice">
          <span class="field-label">Replace map layer</span>
          <select class="field-input" [(ngModel)]="supersedesLayerId" data-testid="drawing-place-layer-choice">
            <option [ngValue]="null" disabled>Choose a layer</option>
            @for (l of data.flaggedLayers; track l.id) { <option [ngValue]="l.id">{{ l.name }}</option> }
          </select>
        </label>
      }
      @if (error) { <p class="error mat-body-1" role="alert" data-testid="drawing-place-error">{{ error }}</p> }
    </div>

    <footer class="place-footer">
      <app-secondary-button buttonId="drawing-place-embedded" [isDisabled]="!canUseEmbedded" (handleClick)="useEmbedded()">Use embedded georeferencing</app-secondary-button>
      <app-primary-button buttonId="drawing-place-submit" data-testid="drawing-place-submit" [isDisabled]="!canPlace" (handleClick)="place()">Place</app-primary-button>
    </footer>
  } @else {
    <div class="place-status" data-testid="drawing-place-status">
      @if (phase === 'placing') { <mat-spinner diameter="32"></mat-spinner> } @else { <mat-icon class="status-icon">error_outline</mat-icon> }
      <p class="mat-body-1" [attr.role]="phase === 'failed' ? 'alert' : 'status'">{{ status }}</p>
      @if (phase === 'failed') {
        <app-secondary-button buttonId="drawing-place-back" (handleClick)="backToPoints()">Back to points</app-secondary-button>
      }
    </div>
  }
</div>
